/**
 * AUD currency helpers — display formatting (CHP-551) + input parsing (CHP-503).
 *
 * Monetary values are stored as integer **cents**. `formatAud` /
 * `formatAudPrecise` are the single source of truth for rendering them
 * (consolidating ~15 local `formatAud` / `formatAudCents` / `formatAudWhole`
 * copies):
 *
 *  - `formatAud` — whole dollars, no decimals ("$310,000", not "$310,000.00").
 *    The design-system default for deal values and pipeline sums.
 *  - `formatAudPrecise` — two decimals ("$6,250.50"). SPIF / incentive amounts
 *    that carry cents.
 *
 * Both use the `"en-AU"` locale, a leading `$`, and return the em-dash "—" for
 * null/undefined so callers can drop their own null guards.
 *
 * `toCleanNumeric` / `formatThousands` are the input-side counterparts used by
 * `CurrencyInput`: the deal outcome forms store a *clean* numeric string (digits
 * + at most one decimal point) so existing `parseFloat(value)` / cents
 * conversion keeps working, while the field shows thousands separators — so a
 * figure typed/pasted with commas ("1,000,000") no longer reaches the API as a
 * non-numeric string and 422s the closed-won submit (CHP-504).
 */

const DASH = "—";

/**
 * Whole-dollar AUD, e.g. `$310,000`. No decimals (`maximumFractionDigits: 0`).
 * Returns "—" when the amount is null/undefined.
 */
export function formatAud(cents: number | null | undefined): string {
  if (cents == null) return DASH;
  return `$${(cents / 100).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;
}

/**
 * AUD to the cent, e.g. `$6,250.50`. Two decimals (`minimumFractionDigits: 2`).
 * Returns "—" when the amount is null/undefined.
 */
export function formatAudPrecise(cents: number | null | undefined): string {
  if (cents == null) return DASH;
  return `$${(cents / 100).toLocaleString("en-AU", { minimumFractionDigits: 2 })}`;
}

/**
 * Strip a display string down to a clean numeric value: digits plus at most one
 * decimal point (the first one wins; later dots are dropped). Everything else
 * — commas, spaces, currency symbols, letters — is removed.
 */
export function toCleanNumeric(raw: string): string {
  let s = raw.replace(/[^\d.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  return s;
}

/**
 * Format a clean numeric string with thousands separators for display,
 * preserving an in-progress decimal portion: "1000" -> "1,000",
 * "1000." -> "1,000.", "1000.5" -> "1,000.5". Empty in -> empty out.
 */
export function formatThousands(clean: string): string {
  if (clean === "") return "";
  const dot = clean.indexOf(".");
  const intPart = dot === -1 ? clean : clean.slice(0, dot);
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return dot === -1 ? grouped : `${grouped}.${clean.slice(dot + 1)}`;
}

/**
 * Convert a dollars string to integer **cents**, robustly — the cents form the
 * deal outcome forms submit to the API (CHP-503/504).
 *
 * `Math.round(parseFloat(value) * 100)` is wrong on half-cent boundaries: IEEE-754
 * makes `parseFloat("2.675") * 100 === 267.4999…`, so `Math.round` yields 267 (=
 * $2.67) instead of 268. We sidestep the float entirely by rounding on the digit
 * string: the first two fractional digits are the cents, and the third fractional
 * digit decides the half-up carry ("…5" and up rounds up — matching `Math.round`'s
 * round-half-up for positive values, but without the binary-rounding error). This
 * also stays exact for large values, where a fixed `Number.EPSILON` nudge would be
 * smaller than the value's ULP and do nothing.
 *
 * Returns `NaN` for non-numeric input so callers can gate on `Number.isFinite`
 * (and `NaN > 0` is `false`, so the existing positive-cents gates stay correct).
 */
export function dollarsToCents(value: string): number {
  const clean = toCleanNumeric(value);
  if (clean === "" || clean === ".") return Number.NaN;
  const dot = clean.indexOf(".");
  const intStr = dot === -1 ? clean : clean.slice(0, dot);
  const fracStr = dot === -1 ? "" : clean.slice(dot + 1);
  const dollars = intStr === "" ? 0 : parseInt(intStr, 10);
  const centsDigits = `${fracStr}00`.slice(0, 2); // first two fractional digits
  let cents = dollars * 100 + parseInt(centsDigits, 10);
  // Half-up round on the third fractional digit (sidesteps IEEE-754).
  const third = fracStr.charAt(2);
  if (third !== "" && third >= "5") cents += 1;
  return cents;
}
